/* assets/src/viewer/materials/library.js
 * Profili PBR + materiali condivisi + cross-fade colore IN-PLACE.
 * Il cross-fade è il cuore del "zero lag" sul cambio colore: nessun tocco alla geometria,
 * solo material.color animato (avanzato dal render loop via update()).
 */
import * as THREE from 'three';
import { clamp, getViewerSettings } from '../settings.js';

// Port di asgGetStructureMaterialProfile (:54-70), con envMapIntensity ritarata per IBL da Sky.
export function structureProfile(structColor) {
  const settings = getViewerSettings();
  const gloss = settings.materialGlossiness;
  const metalness = settings.materialMetalness;
  const baseColor = new THREE.Color(structColor || '#6C6E70');
  // Alluminio verniciato a polvere = prevalentemente dielettrico: bassa metalness così il
  // COLORE scelto resta ben visibile (requisito configuratore), clearcoat per la lucentezza.
  return {
    color: baseColor.clone(),
    roughness: clamp(0.62 - gloss * 0.14, 0.42, 0.72),
    metalness: clamp(0.10 + metalness * 0.18, 0.06, 0.35),
    clearcoat: 0.12 + gloss * 0.18,
    clearcoatRoughness: clamp(0.5 - gloss * 0.12, 0.25, 0.6),
    envMapIntensity: 0.32 + gloss * 0.18,
  };
}

export class MaterialLibrary {
  constructor(textures) {
    this.textures = textures || {};
    this.tweens = [];
  }

  // Metallo struttura: MeshPhysicalMaterial con clearcoat + leggera anisotropy (alluminio verniciato).
  structureMaterial(structColor) {
    const p = structureProfile(structColor);
    const mat = new THREE.MeshPhysicalMaterial({
      color: p.color,
      roughness: p.roughness,
      metalness: p.metalness,
      clearcoat: p.clearcoat,
      clearcoatRoughness: p.clearcoatRoughness,
      envMapIntensity: p.envMapIntensity,
    });
    if (this.textures.metal) {
      mat.bumpMap = this.textures.metal.bump;
      mat.bumpScale = 0.015;
    }
    if ('anisotropy' in mat) mat.anisotropy = 0.3;
    mat.userData.asgRole = 'structure';
    return mat;
  }

  // Telo/tessuto: sheen realistico, opaco, con micro-trama.
  fabricMaterial(fabricColor) {
    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(fabricColor || '#cbd5e1'),
      roughness: 0.92,
      metalness: 0.0,
      sheen: 0.6,
      sheenRoughness: 0.8,
      sheenColor: new THREE.Color(0xffffff),
      envMapIntensity: 0.5,
      side: THREE.DoubleSide,
    });
    if (this.textures.fabric) {
      mat.map = this.textures.fabric.map;
      mat.bumpMap = this.textures.fabric.bump;
      mat.bumpScale = 0.01;
    }
    mat.userData.asgRole = 'fabric';
    return mat;
  }

  // Dettagli metallici scuri (motori, cerniere, profili).
  detailMaterial(hex = 0x2a2f36, { roughness = 0.5, metalness = 0.85 } = {}) {
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hex),
      roughness,
      metalness,
      envMapIntensity: 0.7,
    });
    mat.userData.asgRole = 'detail';
    return mat;
  }

  // Avvia un cross-fade del colore di un materiale verso targetColor (hex int o #string).
  crossfadeColor(material, targetColor, duration = 260) {
    if (!material || !material.color) return;
    const to = new THREE.Color(targetColor);
    const from = material.color.clone();
    if (from.equals(to)) return;
    // sostituisce un eventuale tween in corso sullo stesso materiale
    this.tweens = this.tweens.filter((t) => t.material !== material);
    this.tweens.push({ material, from, to, start: performance.now(), duration });
  }

  // Imposta il colore immediatamente (nessuna animazione) — usato in rebuild/topological.
  setColorImmediate(material, color) {
    if (material && material.color) material.color.set(color);
    this.tweens = this.tweens.filter((t) => t.material !== material);
  }

  // Avanza i tween; ritorna true finché almeno uno è attivo (il loop continua a renderizzare).
  update(now = performance.now()) {
    if (!this.tweens.length) return false;
    const still = [];
    for (const tw of this.tweens) {
      const t = clamp((now - tw.start) / tw.duration, 0, 1);
      // ease-out cubica
      const e = 1 - Math.pow(1 - t, 3);
      tw.material.color.copy(tw.from).lerp(tw.to, e);
      if (t < 1) still.push(tw);
    }
    this.tweens = still;
    return this.tweens.length > 0;
  }
}
